import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Upload, Download, Search, Trash2, ArrowUp, ArrowDown, Minus, FileText, Bot, TrendingUp, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function exportCsv(filename, columns, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(esc).join(',')].concat(rows.map(r => columns.map(c => esc(r[c])).join(',')));
  const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ChangeBadge({ change }) {
  if (change > 0) return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold" data-testid="change-up">
      <ArrowUp className="w-3 h-3" />+{change}
    </span>
  );
  if (change < 0) return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold" data-testid="change-down">
      <ArrowDown className="w-3 h-3" />{change}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold" data-testid="change-flat">
      <Minus className="w-3 h-3" />0
    </span>
  );
}

function UploadCard({ title, description, sampleCsv, sampleXlsx, onUpload, uploading }) {
  return (
    <div className="bg-gradient-to-r from-slate-50/60 to-white rounded-2xl border border-slate-200/60 p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
            <Upload className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-lg">{title}</h3>
            <p className="text-slate-500 text-sm">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={`${API}${sampleCsv}`} download className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="download-sample-csv">
            <Download className="w-4 h-4" /> Sample CSV
          </a>
          <a href={`${API}${sampleXlsx}`} download className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="download-sample-xlsx">
            <Download className="w-4 h-4" /> Sample Excel
          </a>
          <label className="cursor-pointer">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onUpload} disabled={uploading} data-testid="file-upload-input" />
            <span className={`flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload File'}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

function SearchEngineRankingsTab({ firmId, canEdit }) {
  const [items, setItems] = useState([]);
  const [lastUpload, setLastUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [engineFilter, setEngineFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/position-tracking/${firmId}/rankings`, { headers: authHeaders() });
      setItems(res.data.items || []);
      setLastUpload(res.data.last_upload || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load rankings');
    } finally { setLoading(false); }
  }, [firmId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/position-tracking/${firmId}/rankings/upload`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await fetchData();
      alert(`Uploaded ${res.data.rows} rows.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all search rankings data?')) return;
    await axios.delete(`${API}/position-tracking/${firmId}/rankings`, { headers: authHeaders() });
    await fetchData();
  };

  const engines = useMemo(() => Array.from(new Set(items.map(i => i.search_engine).filter(Boolean))), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => {
      if (engineFilter !== 'all' && i.search_engine !== engineFilter) return false;
      if (!q) return true;
      return (i.keyword || '').toLowerCase().includes(q) || (i.landing_page || '').toLowerCase().includes(q);
    });
  }, [items, query, engineFilter]);

  const handleExport = () => {
    const cols = ['Keyword', 'Search Engine', 'Current Position', 'Previous Position', 'Position Change', 'Landing Page'];
    const rows = filtered.map(i => ({
      'Keyword': i.keyword, 'Search Engine': i.search_engine,
      'Current Position': i.current_position, 'Previous Position': i.previous_position,
      'Position Change': i.position_change, 'Landing Page': i.landing_page,
    }));
    exportCsv('search_rankings_export.csv', cols, rows);
  };

  return (
    <div data-testid="search-rankings-tab">
      {canEdit && (
        <UploadCard
          title="Upload Search Engine Rankings"
          description="CSV / XLSX with: Keyword · Search Engine · Current Position · Previous Position · Position Change · Landing Page"
          sampleCsv="/position-tracking/sample/rankings.csv"
          sampleXlsx="/position-tracking/sample/rankings.xlsx"
          onUpload={handleUpload}
          uploading={uploading}
        />
      )}

      {error && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" placeholder="Search keyword or landing page…" value={query} onChange={e => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
              data-testid="rankings-search-input"
            />
          </div>
          <select value={engineFilter} onChange={e => setEngineFilter(e.target.value)}
            className="px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white text-sm"
            data-testid="rankings-engine-filter"
          >
            <option value="all">All Search Engines</option>
            {engines.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {lastUpload && (
            <span className="text-xs text-slate-500">
              Last updated: {new Date(lastUpload.uploaded_at).toLocaleString()} ({lastUpload.rows} rows)
            </span>
          )}
          <button onClick={handleExport} disabled={!filtered.length}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="export-rankings-btn"
          >
            <Download className="w-4 h-4" /> Export
          </button>
          {canEdit && items.length > 0 && (
            <button onClick={handleClear}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-rose-200 text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-50"
              data-testid="clear-rankings-btn"
            >
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !filtered.length ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{items.length ? 'No rows match your filters.' : 'No ranking data uploaded yet.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full modern-table">
              <thead>
                <tr>
                  <th className="px-6 py-4 text-left">Keyword</th>
                  <th className="px-6 py-4 text-left">Search Engine</th>
                  <th className="px-6 py-4 text-center">Current</th>
                  <th className="px-6 py-4 text-center">Previous</th>
                  <th className="px-6 py-4 text-center">Change</th>
                  <th className="px-6 py-4 text-left">Landing Page</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row, idx) => (
                  <tr key={row.id || idx} className="hover:bg-slate-50/50" data-testid={`ranking-row-${idx}`}>
                    <td className="px-6 py-4 font-medium text-slate-800">{row.keyword}</td>
                    <td className="px-6 py-4 text-slate-600">{row.search_engine}</td>
                    <td className="px-6 py-4 text-center font-bold text-slate-900">{row.current_position}</td>
                    <td className="px-6 py-4 text-center text-slate-500">{row.previous_position}</td>
                    <td className="px-6 py-4 text-center"><ChangeBadge change={row.position_change} /></td>
                    <td className="px-6 py-4 text-violet-600 text-sm">
                      {row.landing_page ? <a href={row.landing_page} target="_blank" rel="noopener noreferrer" className="hover:underline truncate inline-block max-w-xs">{row.landing_page}</a> : <span className="text-slate-400">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AIEngineVisibilityTab({ firmId, canEdit }) {
  const [items, setItems] = useState([]);
  const [lastUpload, setLastUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/position-tracking/${firmId}/ai-visibility`, { headers: authHeaders() });
      setItems(res.data.items || []);
      setLastUpload(res.data.last_upload || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load AI visibility');
    } finally { setLoading(false); }
  }, [firmId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setError('');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/position-tracking/${firmId}/ai-visibility/upload`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      await fetchData(); alert(`Uploaded ${res.data.rows} rows.`);
    } catch (err) { setError(err.response?.data?.detail || 'Upload failed.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all AI visibility data?')) return;
    await axios.delete(`${API}/position-tracking/${firmId}/ai-visibility`, { headers: authHeaders() });
    await fetchData();
  };

  const platforms = useMemo(() => Array.from(new Set(items.map(i => i.ai_platform).filter(Boolean))), [items]);
  const statuses = useMemo(() => Array.from(new Set(items.map(i => i.visibility_status).filter(Boolean))), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => {
      if (platformFilter !== 'all' && i.ai_platform !== platformFilter) return false;
      if (statusFilter !== 'all' && i.visibility_status !== statusFilter) return false;
      if (!q) return true;
      return (i.keyword || '').toLowerCase().includes(q) || (i.notes || '').toLowerCase().includes(q);
    });
  }, [items, query, platformFilter, statusFilter]);

  const handleExport = () => {
    const cols = ['Keyword/Query', 'AI Platform', 'Visibility Status', 'Position/Reference', 'Notes / Observations'];
    const rows = filtered.map(i => ({
      'Keyword/Query': i.keyword, 'AI Platform': i.ai_platform,
      'Visibility Status': i.visibility_status, 'Position/Reference': i.position_reference,
      'Notes / Observations': i.notes,
    }));
    exportCsv('ai_engine_visibility_export.csv', cols, rows);
  };

  const statusBadgeClass = (s) => {
    const t = (s || '').toLowerCase();
    if (t.includes('visible') && !t.includes('not') && !t.includes('partial')) return 'bg-emerald-100 text-emerald-700';
    if (t.includes('partial')) return 'bg-amber-100 text-amber-700';
    if (t.includes('not')) return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div data-testid="ai-visibility-tab">
      {canEdit && (
        <UploadCard
          title="Upload AI Engine Visibility"
          description="CSV / XLSX with: Keyword/Query · AI Platform · Visibility Status · Position/Reference · Notes/Observations"
          sampleCsv="/position-tracking/sample/ai-visibility.csv"
          sampleXlsx="/position-tracking/sample/ai-visibility.xlsx"
          onUpload={handleUpload}
          uploading={uploading}
        />
      )}

      {error && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" placeholder="Search keyword or notes…" value={query} onChange={e => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
              data-testid="ai-search-input"
            />
          </div>
          <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm" data-testid="ai-platform-filter">
            <option value="all">All Platforms</option>
            {platforms.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm" data-testid="ai-status-filter">
            <option value="all">All Statuses</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {lastUpload && <span className="text-xs text-slate-500">Last updated: {new Date(lastUpload.uploaded_at).toLocaleString()} ({lastUpload.rows} rows)</span>}
          <button onClick={handleExport} disabled={!filtered.length}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="export-ai-btn"
          >
            <Download className="w-4 h-4" /> Export
          </button>
          {canEdit && items.length > 0 && (
            <button onClick={handleClear} className="flex items-center gap-2 px-3 py-2 bg-white border border-rose-200 text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-50" data-testid="clear-ai-btn">
              <Trash2 className="w-4 h-4" /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : !filtered.length ? (
          <div className="p-12 text-center">
            <Bot className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{items.length ? 'No rows match your filters.' : 'No AI visibility data uploaded yet.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full modern-table">
              <thead>
                <tr>
                  <th className="px-6 py-4 text-left">Keyword / Query</th>
                  <th className="px-6 py-4 text-left">AI Platform</th>
                  <th className="px-6 py-4 text-left">Visibility</th>
                  <th className="px-6 py-4 text-left">Position / Reference</th>
                  <th className="px-6 py-4 text-left">Notes / Observations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row, idx) => (
                  <tr key={row.id || idx} className="hover:bg-slate-50/50 align-top" data-testid={`ai-row-${idx}`}>
                    <td className="px-6 py-4 font-medium text-slate-800">{row.keyword}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs font-semibold">{row.ai_platform || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(row.visibility_status)}`}>{row.visibility_status || '-'}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">{row.position_reference || '-'}</td>
                    <td className="px-6 py-4 text-slate-600 text-sm max-w-md">{row.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PositionTracking({ user, selectedClient }) {
  const [activeTab, setActiveTab] = useState('search-engines');
  const firmId = selectedClient?.firmId || selectedClient?.firm_id || user?.firm_id || user?.firm?.id;
  const canEdit = ['super_admin', 'internal_team'].includes(user?.role);

  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="position-tracking-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Position Tracking</h1>
        <p className="text-slate-500 text-lg">Monitor Search Engine Rankings and AI Engine Visibility through manual data uploads.</p>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow-lg">
        <div className="border-b border-slate-200/50 bg-gradient-to-r from-slate-50/50 to-white/50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('search-engines')}
              className={`px-6 py-4 font-medium transition-all flex items-center gap-2 ${activeTab === 'search-engines' ? 'text-violet-600 border-b-2 border-violet-600 bg-white/50' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid="tab-search-engines"
            >
              <TrendingUp className="w-4 h-4" /> Search Engine Rankings
            </button>
            <button
              onClick={() => setActiveTab('ai-engines')}
              className={`px-6 py-4 font-medium transition-all flex items-center gap-2 ${activeTab === 'ai-engines' ? 'text-violet-600 border-b-2 border-violet-600 bg-white/50' : 'text-slate-500 hover:text-slate-800'}`}
              data-testid="tab-ai-engines"
            >
              <Bot className="w-4 h-4" /> AI Engine Visibility
            </button>
          </div>
        </div>
        <div className="p-6">
          {!firmId ? (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" /> Select a client to view data.</div>
          ) : activeTab === 'search-engines' ? (
            <SearchEngineRankingsTab firmId={firmId} canEdit={canEdit} />
          ) : (
            <AIEngineVisibilityTab firmId={firmId} canEdit={canEdit} />
          )}
        </div>
      </div>
    </div>
  );
}
